import moment from "moment";
import { formatMoney } from "../../features/utils/formatMoney";
import { getSupplierName } from "../flight/supplier/getSupplierName";
import { getSupplierClass } from "../../components/flight/FlightOfferDisplay";
import { flightStatusMap } from "../../pages/Orders/OrdersData";
import { Link } from "react-router-dom";
import { getChangeStatusName } from "../flight/order/getStatusName";
import { getChangeAdminStage } from "../flight/order/getChangeAdminGuidance";
import OrderStatus from "../../components/order/OrderStatus";
import { getFlightFareTypeTagElement } from "./flightFareTypeTag";
import {
  getFlightBookingFromChange,
  getChangeRequestPassengers,
} from "../flight/order/getRequestedDateChange";
import {
  getDetailsChangeSummary,
  travellerName,
} from "../flight/order/getDetailsChangeSummary";

function getTicketNumbers(flightData) {
  const tickets = (flightData?.travelers || [])
    .flatMap((traveler) => traveler?.ticketNumbers || [])
    .filter(Boolean);
  return [...new Set(tickets)];
}

function getTicketNumbersForChange(data, flightData) {
  const info = Array.isArray(data?.passengerInfo) ? data.passengerInfo : [];
  if (info.length) {
    const ids = new Set(info.map((p) => String(p?.id)).filter(Boolean));
    const fromBooking = (flightData?.travelers || []).filter((t) =>
      ids.has(String(t?.id)),
    );
    const tickets = fromBooking
      .flatMap((traveler) => traveler?.ticketNumbers || [])
      .filter(Boolean);
    if (tickets.length) return [...new Set(tickets)];
  }
  return getTicketNumbers(flightData);
}

export function templateFlightOrderRenameData(data) {
  const flightData = getFlightBookingFromChange(data);
  let flightType = "One Way";
  if (flightData?.destinations?.length) {
    flightType = "Multi City";
    if (
      flightData?.destinations?.at(-1)?.to ===
      flightData?.destinations?.at(0)?.from
    ) {
      flightType = "Return";
    }
  }

  const tickets = getTicketNumbersForChange(data, flightData);
  const correction = getDetailsChangeSummary(data);
  const fromInfo = (data?.passengerInfo || [])
    .map((p) => travellerName(p))
    .filter(Boolean);
  const passengerList = (
    fromInfo.length ? fromInfo : getChangeRequestPassengers(data, flightData)
  ).filter(Boolean);
  const passengerNote =
    passengerList.length === 1
      ? "1 passenger on this change"
      : passengerList.length > 1
        ? `${passengerList.length} passengers on this change`
        : "";

  const detailPath =
    "/order/flight/ticket/rename/confirmation/" +
    (data?.status === "Success" || data?.status === "NameChangeCompleted"
      ? "details/"
      : "") +
    data?._id +
    "?page=Details Change";

  return [
    {
      elements: [
        {
          type: "title",
          value: moment(data?.createdAt)?.format("DD-MM-YYYY HH:mm [GMT]Z"),
        },
        {
          type: "copy",
          value: data?.orderId || flightData?.orderId || data?.bookingId,
          renderValue: () => (
            <Link target="_blank" to={detailPath}>
              {data?.orderId || flightData?.orderId || data?.bookingId || "-"}
            </Link>
          ),
        },
        {
          type: "text",
          value: flightData?.bookingId ? (
            <small className="text-gray-500">Booking {flightData.bookingId}</small>
          ) : (
            ""
          ),
        },
        {
          type: "tag",
          value: getSupplierName(flightData?.supplier),
          className: getSupplierClass(flightData?.supplier),
          color: "text-[#BD002A]",
          background: "bg-[#BD002A]/20",
        },
        getFlightFareTypeTagElement(flightData),
        {
          type: "Agent",
          render: () => (
            <div className="py-2">
              <small>
                {(data?.account?.firstName || "") +
                  " " +
                  (data?.account?.lastName || "")}
              </small>
              <div
                className="w-[200px] overflow-hidden whitespace-nowrap text-ellipsis"
                title={data?.account?.detail?.tradingName}
              >
                {data?.account?.detail?.tradingName}
              </div>
            </div>
          ),
        },
      ],
      value: moment(data?.createdAt)?.format("YYYY-MM-DD HH:mm"),
      width: "md:w-[22%]",
    },
    {
      elements: [
        { type: "title", value: "Correction" },
        {
          type: "text",
          render: () => (
            <div className="flex flex-col gap-1 text-sm">
              <span className="inline-flex w-fit rounded-md bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-900">
                {correction.label}
              </span>
              {correction.detail ? (
                <div className="leading-snug">{correction.detail}</div>
              ) : null}
              <small className="text-gray-500">
                Submitted {moment(data?.createdAt)?.format("D MMM YYYY HH:mm")}
              </small>
            </div>
          ),
        },
      ],
      value: correction.label,
      width: "md:w-[18%]",
    },
    {
      elements: [
        { type: "title", value: flightType },
        {
          type: "text",
          render: () => (
            <div>
              {flightData?.destinations
                ?.slice(0, 1)
                ?.map((obj) => `${obj?.from} - ${obj?.to}`)
                ?.join(",") || ""}
              <div>
                <span className="text-theme1">{flightData?.pnr}</span>
              </div>
              <small>{flightData?.flights?.at(0)?.airlineName}</small>
            </div>
          ),
        },
      ],
      width: "md:w-[12%]",
    },
    {
      elements: [
        { type: "title", value: "Ticket" },
        {
          type: "text",
          value: tickets.length ? tickets.join(" | ") : "-",
        },
        {
          type: "text",
          value: flightData?.status ? (
            <small className="text-gray-500">{flightData.status}</small>
          ) : (
            ""
          ),
        },
      ],
      value: tickets.join(" | "),
      width: "md:w-[12%]",
    },
    {
      elements: [
        { type: "title", value: "GDS PNR" },
        { type: "copy", value: flightData?.pnr },
        { type: "title", value: "Airline PNR" },
        { type: "copy", value: flightData?.pnr },
      ],
      width: "md:w-[12%]",
    },
    {
      elements: [
        { type: "title", value: "Passenger" },
        {
          type: "text",
          render: () =>
            passengerList.length ? (
              <div className="flex flex-col gap-1 items-start">
                {passengerList.map((name) => (
                  <span
                    key={name}
                    className="inline-flex w-fit rounded-md bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-900"
                  >
                    {name}
                  </span>
                ))}
                {passengerNote ? (
                  <small className="text-gray-500">{passengerNote}</small>
                ) : null}
              </div>
            ) : (
              "-"
            ),
        },
      ],
      value: passengerList.join(", "),
      width: "md:w-[12%]",
    },
    {
      elements: [
        { type: "title", value: "Fee" },
        {
          type: "text",
          value: data?.payable ? formatMoney(data?.payable) : "-",
        },
      ],
      value: data?.payable || flightData?.payable,
      width: "md:w-[10%]",
    },
    {
      elements: [
        { type: "title", value: "Status" },
        {
          type: "status",
          value: getChangeStatusName(flightStatusMap(data?.status), data),
          render: () => {
            const status = flightStatusMap(data?.status);
            const stage = getChangeAdminStage(data);
            const assigned = data?.tasks?.at(0);
            const showAssignment =
              assigned &&
              ["needs_review", "needs_quote"].includes(stage) &&
              [
                "Approved",
                "Pending",
                "ManualReviewRequired",
                "NameChangeRequestSubmitted",
                "ManualNameChangeProcessing",
              ].includes(status);
            return (
              <div className={`rounded-md mb-3`}>
                <span
                  className={`inline-block overflow-hidden text-ellipsis text-xs `}
                  title={status}
                >
                  {showAssignment ? (
                    stage === "needs_quote" ? (
                      !["ToDo"]?.includes(assigned?.taskStatus) ? (
                        `Awaiting Quote by ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                      ) : (
                        `Assigned to ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                      )
                    ) : ["ToDo"]?.includes(assigned?.taskStatus) ? (
                      `Awaiting acceptance by ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                    ) : (
                      `Under Review by ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                    )
                  ) : (
                    <OrderStatus value={getChangeStatusName(status, data)} />
                  )}
                </span>
              </div>
            );
          },
        },
      ],
      value: (function () {
        return [
          "Pending",
          "Denied",
          "Approved",
          "PendingChange",
          "PendingReissue",
        ]?.findIndex((val) => val === flightStatusMap(data?.status));
      })(),
      width: "md:w-[14%]",
    },
  ];
}
